import {
    ActionIcon,
    Anchor,
    Button,
    Flex,
    InputLabel,
    Radio,
    Stack,
    Text,
    TextInput,
    Tooltip,
    UnstyledButton,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { type JSX } from "react";

import type { CommunicationType } from "../../../../../types";

import { RadioButton } from "../../SendLink/RadioButton";

export interface GuidedBrowsingContentViewProps {
    commType: CommunicationType;
    contact: string;
    isEmailType: boolean;
    loading: boolean;
    isSendDisabled: boolean;
    isOverrideDisabled: boolean;
    showOverrideAction: boolean;
    showViewSentLinkAction: boolean;
    showCommTypeSelector?: boolean;
    /** Validation message for the contact field (invalid email / phone); undefined hides it. */
    error?: string;
    onCommTypeChange: (value: string) => void;
    onContactChange: (value: string) => void;
    sendLinkCopy?: string;
    sendLinkSubCopy?: string;
    onSend: () => void;
    onSendOverride: () => void;
    onViewSentLink: () => void;
}

export function GuidedBrowsingContentView({
    commType,
    contact,
    isEmailType,
    loading,
    isSendDisabled,
    isOverrideDisabled,
    showOverrideAction,
    showViewSentLinkAction,
    showCommTypeSelector = true,
    error,
    sendLinkCopy,
    sendLinkSubCopy,
    onCommTypeChange,
    onContactChange,
    onSend,
    onSendOverride,
    onViewSentLink,
}: GuidedBrowsingContentViewProps): JSX.Element {
    return (
        <Stack gap="xs" pt="xs">
            <Text size="sm" fw={500}>
                {sendLinkCopy ?? "Send a link to product details"}
            </Text>
            {sendLinkSubCopy && (
                <Text size="xs" c="dark.1">
                    {sendLinkSubCopy}
                </Text>
            )}
            {showCommTypeSelector && (
                <Radio.Group value={commType} onChange={onCommTypeChange}>
                    <Flex direction="column" gap="xs">
                        <RadioButton value="SMS">
                            <Text size="xs">
                                <strong>Send text</strong>
                            </Text>
                        </RadioButton>
                        <RadioButton value="EMAIL">
                            <Text size="xs">
                                <strong>Email link</strong>
                            </Text>
                        </RadioButton>
                    </Flex>
                </Radio.Group>
            )}
            <Flex direction="column" gap="3xs">
                <InputLabel size="xs">{isEmailType ? "Email address" : "Phone number"}</InputLabel>
                <Flex gap="xs" align="flex-start">
                    <TextInput
                        className="fs-mask"
                        size="sm"
                        value={contact}
                        onChange={(event) => onContactChange(event.currentTarget.value)}
                        placeholder={isEmailType ? "Enter email address" : "Enter phone number"}
                        error={error}
                        style={{ flex: 1 }}
                    />
                    <Button onClick={onSend} loading={loading} disabled={isSendDisabled} size="sm" variant="light">
                        Send link
                    </Button>
                </Flex>
            </Flex>
            {showOverrideAction && (
                <UnstyledButton onClick={onSendOverride} w="100%" disabled={isOverrideDisabled}>
                    <Flex align="center" justify="center" gap="xs">
                        <Anchor c="primary.1" size="xxs" underline="always">
                            Send without checklist items
                        </Anchor>
                        <Tooltip
                            label="Sending without completing checklist items is only for malfunctions with checklist auto-detection. We will be notified if this is used excessively."
                            multiline
                            w={220}
                        >
                            <ActionIcon variant="transparent" size="xs" c="primary.1">
                                <IconInfoCircle size={14} />
                            </ActionIcon>
                        </Tooltip>
                    </Flex>
                </UnstyledButton>
            )}
            {showViewSentLinkAction && (
                <UnstyledButton onClick={onViewSentLink} w="100%">
                    <Flex align="center" justify="center">
                        <Anchor c="primary.1" size="xxs" underline="always">
                            View sent link
                        </Anchor>
                    </Flex>
                </UnstyledButton>
            )}
        </Stack>
    );
}
